import { getCurrentInstance } from 'vue';
// eslint-disable-next-line no-restricted-imports
import { useI18n as useVueI18n, type Composer, type NamedValue, type TranslateOptions } from 'vue-i18n';

type I18nComposer = ReturnType<typeof useVueI18n>;
type TValues = NamedValue | unknown[] | number;

/** Global i18n composer instance.
 * If set this instance will be used instead of the useI18n() provider.
 * ⚠️ Safe because the apps are SPA-only (one app per page, no SSR request sharing).
 */
let globalComposer: I18nComposer | undefined;

/** Registers the app's global composer so useI18n() also works outside component setup. */
export function setGlobalComposer(composer: Composer) {
  globalComposer = composer as I18nComposer;
}

/** Use the global hoisted composer if set. Otherwise defaults to the vue-i18n provider.
 * This allow to use the i18n composer in context outside of components (like Nuxt middlewares)
*/
function resolveComposer(...args: Parameters<typeof useVueI18n>): I18nComposer {
  if (getCurrentInstance()) return useVueI18n(...args);
  return globalComposer ?? useVueI18n(...args);
}

/** useI18n override that also works outside component setup (middlewares, stores, plugins), plus `tf`. */
export function useI18n(...useI18nArgs: Parameters<typeof useVueI18n>) {
  const i18n = resolveComposer(...useI18nArgs);
  const { t, te } = i18n;

  /**
   * Translates a key when it exists. `tf` stands for "translation fallback":
   * when the key is missing, it returns the final string argument as a fallback,
   * or `undefined` when no fallback is provided.
   *
   * Values and options use the same arguments as vue-i18n's `t` function.
   */
  function tf(key: string, ...args: [values: TValues, options: TranslateOptions, fallback: string]): string;
  function tf(key: string, ...args: [values: TValues, fallback: string]): string;
  function tf(key: string, ...args: [fallback: string]): string;
  function tf(key: string, ...args: [values: TValues, options: TranslateOptions]): string | undefined;
  function tf(key: string, ...args: [values: TValues]): string | undefined;
  function tf(key: string, ...args: []): string | undefined;
  function tf(key: string, ...args: unknown[]): string | undefined {
    const lastArg = args.at(-1);
    const hasFallback = typeof lastArg === 'string';
    const fallback = hasFallback ? (lastArg as string) : undefined;
    const tArgs = hasFallback ? args.slice(0, -1) : args;

    if (!te(key)) return fallback;
    return (t as (key: string, ...args: unknown[]) => string)(key, ...tArgs);
  }

  return Object.assign(i18n, { tf });
}
